<script lang="ts">
  /**
   * Vekstlys-arket — flyttet ut av gamle LysKontroll og presentert i ark-form
   * (design-handoff juni 2026). Sol-bue + DLI + intensitet/timer + anbefaling +
   * per-plante helsesjekk. Lagrer til potte_commands ved «Ferdig».
   */
  import { onDestroy, untrack } from 'svelte';
  import { supabase } from '../lib/supabase';
  import type { Plante, PotteCommand } from '../lib/database.types';
  import {
    anbefaltInnstilling,
    beregnDli,
    spiringsdag,
    SPIRING_DAGER,
    vurderLysKompatibilitet,
    vurderPlanteDli,
    vurderVannKompatibilitet,
  } from '../lib/lys';
  import { lysVarighetTimer } from '../lib/tid';
  import { lysEnergi } from '../lib/energi';
  import {
    strompris,
    ppfdMaks,
    lysAvstander,
    settLysAvstand,
    LYS,
  } from '../lib/settings';
  import { visFeil, visOk } from '../lib/toast';
  import SolBue from './viz/SolBue.svelte';
  import { ppfdVedAvstand, standardAvstand } from '../lib/lysavstand';

  let {
    potteId,
    planter,
    command,
    now = new Date(),
    plantetDatoer = [],
    onLagret,
  }: {
    potteId: string;
    planter: Plante[];
    command: PotteCommand | null;
    now?: Date;
    /** Plantedatoene i kassa: de første dagene etter såing anbefales spiringslys. */
    plantetDatoer?: (string | null)[];
    onLagret: () => void;
  } = $props();

  let intensitet = $state(70);
  let timer_on = $state('07:00');
  let timer_off = $state('23:00');
  let lagrer = $state(false);
  let avstand = $state(untrack(() => $lysAvstander[potteId] ?? standardAvstand(potteId)));
  const avstandGyldig = $derived(Number.isFinite(avstand) && avstand >= 10 && avstand <= 20);
  const aktuellPpfd = $derived(ppfdVedAvstand(avstand, $ppfdMaks));

  let lastCommandId = $state<string | null>(null);
  $effect(() => {
    if (command && command.id !== lastCommandId) {
      intensitet = command.intensitet;
      timer_on = command.timer_on;
      timer_off = command.timer_off;
      lastCommandId = command.id;
    }
  });

  // Lukkes arket uten «Ferdig», forsvinner endringene. Før skjedde det uten et
  // ord, så det så ut som lyset var endret. Nå sier appen fra.
  const endret = $derived(
    avstand !== ($lysAvstander[potteId] ?? standardAvstand(potteId)) ||
    intensitet !== (command?.intensitet ?? 70) ||
      timer_on !== (command?.timer_on ?? '07:00') ||
      timer_off !== (command?.timer_off ?? '23:00'),
  );
  let endretVedLukking = false; // speiler `endret`, leses i onDestroy
  let lagret = false;
  $effect(() => {
    endretVedLukking = endret;
  });
  onDestroy(() => {
    if (endretVedLukking && !lagret) {
      visFeil('Endringene i vekstlyset ble ikke lagret. Åpne arket igjen og trykk «Ferdig».');
    }
  });

  const beregnetTimer = $derived(Math.round(lysVarighetTimer(timer_on, timer_off) * 10) / 10);
  // Lik på- og av-tid = «ingen lystid» for firmwaren (lyset står av hele døgnet).
  const likeTider = $derived(!!timer_on && !!timer_off && timer_on === timer_off);
  const dliEstimat = $derived(beregnDli(intensitet, beregnetTimer, aktuellPpfd));
  const energi = $derived(lysEnergi(intensitet, beregnetTimer, $strompris, LYS.watt));

  const spiringDag = $derived(spiringsdag(plantetDatoer, now));
  const anbefalt = $derived(anbefaltInnstilling(planter, aktuellPpfd, spiringDag !== null));
  const lysRapport = $derived(vurderLysKompatibilitet(planter));
  const vannRapport = $derived(vurderVannKompatibilitet(planter));
  // Slank advarsel kun ved reelt problem (bevart fra gamle LysKontroll).
  const visAdvarsel = $derived(
    lysRapport.niva === 'risikabel' ||
      lysRapport.niva === 'inkompatibel' ||
      vannRapport.niva !== 'ok',
  );

  // Delt vurdering (lib/lys) — samme regler som oversiktens lys-varsel bruker.
  const planteHelse = $derived(vurderPlanteDli(planter, dliEstimat));

  function brukAnbefaling() {
    intensitet = anbefalt.intensitet;
    timer_on = anbefalt.timer_on;
    timer_off = anbefalt.timer_off;
  }

  async function lagre() {
    if (!avstandGyldig) { visFeil('Velg en avstand mellom 10 og 20 cm.'); return; }
    // Tomt time-felt (bruker slettet innholdet) ville lagret "" → firmware
    // tolker det som «ingen lystid» og lyset blir bare stående av uten forklaring.
    if (!timer_on || !timer_off) {
      visFeil('Sett både «Lys på» og «Lys av» før du lagrer.');
      return;
    }
    lagrer = true;
    const { error } = await supabase.from('potte_commands').upsert(
      { potte_id: potteId, intensitet, timer_on, timer_off, updated_at: new Date().toISOString() },
      { onConflict: 'potte_id' },
    );
    lagrer = false;
    if (error) {
      visFeil('Kunne ikke lagre lysinnstillingen — sjekk nettet og trykk «Ferdig» igjen.');
    } else {
      settLysAvstand(potteId, avstand);
      visOk('Lysinnstilling lagret — potta henter innen 5 sek');
      lagret = true;
      onLagret();
    }
  }

  const statusBadge: Record<string, string> = {
    optimal: 'bg-leaf/[0.12] text-leaf-glow border-leaf/30',
    akseptabel: 'bg-sun/[0.12] text-sun border-sun/30',
    lavt: 'bg-rose/[0.12] text-rose border-rose/30',
    hoyt: 'bg-rose/[0.12] text-rose border-rose/30',
  };
  const statusTekst: Record<string, string> = {
    optimal: 'Bra',
    akseptabel: 'Akseptabelt',
    lavt: 'For lite',
    hoyt: 'For mye',
  };
</script>

<div class="flex items-start justify-between gap-3.5">
  <div>
    <div class="font-display text-[25px] font-semibold leading-[1.05]">Vekstlys</div>
    <div class="font-mono text-[11px] text-text-muted mt-1">
      {LYS.kortNavn} · {String(beregnetTimer).replace('.', ',')} t lys
    </div>
  </div>
  <div
    class="inline-flex items-center gap-1.5 px-[11px] py-[5px] rounded-full bg-leaf/[0.12] border border-leaf/30 font-mono text-[11px] text-leaf-glow whitespace-nowrap"
  >
    {dliEstimat.toFixed(1).replace('.', ',')} DLI
  </div>
</div>

{#if visAdvarsel}
  <div
    class="mt-3 p-2.5 rounded-lg border text-[12px] leading-snug {lysRapport.niva === 'inkompatibel' ||
    vannRapport.niva === 'inkompatibel'
      ? 'border-rose/30 bg-rose/10 text-rose'
      : 'border-sun/30 bg-sun/[0.08] text-sun'}"
  >
    {#if lysRapport.niva === 'risikabel' || lysRapport.niva === 'inkompatibel'}
      <div>⚠ {lysRapport.melding}</div>
    {/if}
    {#if vannRapport.niva !== 'ok'}
      <div class="mt-0.5">💧 {vannRapport.melding}</div>
    {/if}
  </div>
{/if}

<div class="mt-[18px]">
  <SolBue timerOn={timer_on} timerOff={timer_off} {now} />
  <div class="text-center mt-1">
    <div class="font-display text-[46px] font-semibold leading-none">
      {dliEstimat.toFixed(1).replace('.', ',')}
    </div>
    <div class="font-mono text-[10px] text-text-muted mt-0.5">DLI · mol/m² per døgn</div>
  </div>
</div>
<div class="text-center text-[11px] text-text-dim mt-1.5 mb-4">
  Forhåndsvisning oppdateres mens du justerer
</div>

<div class="mb-4">
  <label for="lys-avstand" class="label block mb-1.5">Avstand fra lys til plantetopp</label>
  <div class="flex items-center gap-2">
    <input id="lys-avstand" class="input !w-24 font-mono" type="number" min="10" max="20" step="0.5" bind:value={avstand} />
    <span class="text-sm text-text-muted">cm</span>
  </div>
  <p class="text-[12px] text-text-muted mt-1.5">Fra undersiden av LED-barene. Før spiring: mål til jorda. Oppdater når plantene vokser. Avstanden huskes på denne enheten.</p>
  {#if !avstandGyldig}<p class="text-[12px] text-rose mt-1" role="alert">Målt område er 10–20 cm.</p>{/if}
</div>

<div class="flex items-baseline justify-between mb-2">
  <span class="font-mono text-[11px] tracking-[0.08em] text-text-muted">INTENSITET</span>
  <span class="font-display text-lg font-semibold text-leaf">{intensitet}%</span>
</div>
<div class="relative">
  <input
    type="range"
    min="0"
    max="100"
    bind:value={intensitet}
    class="slider-lys w-full relative z-10"
    style="--pct: {intensitet}%"
    aria-label="Lysintensitet i prosent"
  />
  {#if planter.length > 0}
    <!-- Markør for anbefalt intensitet — se ditt valg mot fasiten på ett blikk. -->
    <div
      class="absolute inset-y-0 flex items-center pointer-events-none"
      style="left: {anbefalt.intensitet}%; transform: translateX(-50%)"
      aria-hidden="true"
    >
      <div class="w-[3px] h-3.5 rounded-full bg-sun/80 ring-1 ring-bg"></div>
    </div>
  {/if}
</div>
<div class="flex justify-between font-mono text-[10px] text-text-dim mt-1 px-0.5">
  <span>Svak</span>
  {#if planter.length > 0}
    <span class="text-sun/90">▲ anbefalt {anbefalt.intensitet}%</span>
  {/if}
  <span>Sterk</span>
</div>

<div class="grid grid-cols-2 gap-3 mt-3.5">
  <div>
    <label for="lys-paa" class="block font-mono text-[11px] tracking-[0.08em] text-text-muted mb-1.5">LYS PÅ</label>
    <input id="lys-paa" type="time" bind:value={timer_on} class="input font-mono" />
  </div>
  <div>
    <label for="lys-av" class="block font-mono text-[11px] tracking-[0.08em] text-text-muted mb-1.5">LYS AV</label>
    <input id="lys-av" type="time" bind:value={timer_off} class="input font-mono" />
  </div>
</div>
{#if likeTider}
  <div class="mt-2 p-2.5 rounded-lg border border-sun/30 bg-sun/[0.08] text-[12px] text-sun leading-snug" role="status">
    Lys på og lys av er samme klokkeslett — da står lyset av hele døgnet.
  </div>
{/if}

<!-- Strøm og kalibrering vises bare her. Begge endres under Innstillinger
     (tannhjulet øverst), så de ikke endres ved et uhell mens man justerer lyset. -->
<div class="mt-3.5 text-center font-mono text-[10.5px] text-text-dim leading-relaxed">
  <div>⚡ {LYS.watt} W · ≈ {energi.kwhPerManed.toFixed(1).replace('.', ',')} kWh/mnd · ~{energi.krPerManed} kr/mnd</div>
  <div>☀️ Estimat: {aktuellPpfd} µmol/m²/s ved 100 % og {String(avstand).replace('.', ',')} cm</div>
  <div>Basert på Photone-punkter ved 10, 13,5 og 20 cm. Mellom høydene er det et anslag.</div>
</div>

{#if planter.length > 0}
  <div
    class="mt-[18px] flex items-center justify-between gap-3 p-3.5 rounded-xl bg-bg-subtle border border-border"
  >
    <div>
      <div class="label mb-0.5">Anbefalt intensitet</div>
      <div class="font-display text-[24px] font-semibold leading-none">{anbefalt.intensitet}%</div>
      <div class="font-mono text-[9.5px] text-text-dim mt-1">
        {anbefalt.spiring ? `Spiring, dag ${spiringDag} av ${SPIRING_DAGER}` : 'Basert på dine planters DLI-behov'}
      </div>
    </div>
    <button class="btn-secondary !py-2 text-xs whitespace-nowrap" onclick={brukAnbefaling}>
      Bruk anbefaling
    </button>
  </div>
  {#if anbefalt.spiring}
    <p class="mt-2 text-[12px] text-text-muted leading-snug">
      Frøene trenger lite lys for å spire. Mindre lys gir mindre varme og uttørking av overflaten.
      Fra dag {SPIRING_DAGER + 1} anbefales {anbefalt.vekstIntensitet} %, eller tidligere når spirene er oppe.
    </p>
  {/if}

  <div class="mt-4">
    <div class="label mb-2.5">Plantene med denne innstillingen</div>
    <!-- Nøkkel med indeks: samme plante kan stå på to plasser (to basilikum),
         og like nøkler i en keyed each gir feil i Svelte 5. -->
    {#each planteHelse as { plante, status, dliOpt }, i (i)}
      <div class="flex items-center gap-2.5 py-2.5 border-b border-surface-raised">
        <span class="w-[7px] h-[7px] rounded-full bg-leaf shrink-0"></span>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold line-clamp-2 break-words">{plante.navn}</div>
          <div class="font-mono text-[9.5px] text-text-dim mt-px">DLI opt {dliOpt}</div>
        </div>
        <span
          class="shrink-0 px-2.5 py-[3px] rounded-full text-[11px] font-semibold border {statusBadge[status]}"
        >
          {statusTekst[status]}
        </span>
      </div>
    {/each}
  </div>
{:else}
  <div class="mt-4 text-sm text-text-muted">Legg til planter for å se anbefalte innstillinger.</div>
{/if}

<button class="btn-primary w-full mt-[18px]" onclick={lagre} disabled={lagrer}>
  {lagrer ? 'Lagrer…' : 'Ferdig'}
</button>
